import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { lazy, Suspense, type ReactNode } from 'react'

import { env, PERSISTED_QUERY_ROOTS, QUERY_CACHE_STORAGE_KEY } from '@/shared/config'
import '@/shared/i18n'
import { Toaster } from '@/shared/ui/toast'

import { ErrorBoundary } from './error-boundary'
import { queryClient } from './query-client'

/** Devtools ship in development builds only — they expose every cached payload. */
const QueryDevtools = lazy(() =>
  import('@tanstack/react-query-devtools').then((module) => ({
    default: module.ReactQueryDevtools,
  })),
)

const persister = createSyncStoragePersister({
  storage: globalThis.localStorage,
  key: QUERY_CACHE_STORAGE_KEY,
})

/**
 * The composition root. Everything is wired here and nowhere else — this is
 * the dependency injection, and there is no DI container.
 */
export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister,
          maxAge: 24 * 60 * 60 * 1000,
          dehydrateOptions: {
            /**
             * An explicit allowlist. Persisting the whole cache would write
             * personal and payment data into localStorage on every render.
             */
            shouldDehydrateQuery: (query) => {
              const root = query.queryKey[0]
              return typeof root === 'string' && PERSISTED_QUERY_ROOTS.includes(root)
            },
          },
        }}
      >
        {/*
         * Base UI's Toaster is a provider, not a sibling widget: `toast()` only
         * reaches a viewport rendered inside it. It takes no `theme` prop —
         * the toast surface is styled from the same tokens as every other
         * popover, so it follows `data-theme` on its own.
         */}
        <Toaster>
          {children}
          {env.isDev && (
            <Suspense fallback={null}>
              <QueryDevtools buttonPosition="bottom-left" />
            </Suspense>
          )}
        </Toaster>
      </PersistQueryClientProvider>
    </ErrorBoundary>
  )
}
